import { auth, endSignupSession } from './auth';
import { takesSession } from './returnTo';
import { withOrgHint } from './orgHint';
import { someoneElsesKeycloakSession } from './followSignInLink';
import { tabStore } from './tabStore';

/*
 * Where signup ends. An email signup leaves no Keycloak browser session for
 * the next app to find, so the console and the partner site get it handed
 * over (ui-auth-lib's handOff: in the fragment, taken and stripped by their
 * restore()). Only our own origins get it, with the new organization as a hint.
 */
export function handoffUrl(target: string, organizationName?: string): string {
    if (!takesSession(target)) return target;
    // Both open in the organization named (the console's boot and the partner site adopt ?org=).
    const hinted = organizationName ? withOrgHint(target, organizationName) : target;
    return auth.handOff(hinted);
}

type Leaving = { target: string; organizationName?: string; endSignup?: boolean };

const WAITING = 'dodil_handoff_waiting';
/** Long enough for Keycloak's sign-out round trip; a later visit to /signup/continue isn't a hand-off. */
export const HANDOFF_FRESH_MS = 5 * 60_000;

/*
 * Off to `target` with this session. The receiving app follows Keycloak's live
 * session over a handed-over one for a different person (ui-auth-lib's
 * restore()), so if this browser's Keycloak session is someone else's, it is
 * ended first: the destination waits in the tab (only the address, never a
 * token) and /signup/continue hands over on the way back. The new account
 * lands as itself, with or without iam's one-time link.
 */
export async function leaveWithSession(leaving: Leaving): Promise<void> {
    if (takesSession(leaving.target) && auth.getAccessToken()) {
        const holder = await someoneElsesKeycloakSession();
        if (holder) {
            tabStore.set(WAITING, JSON.stringify({ ...leaving, at: Date.now() }));
            auth.endKeycloakSession(holder, `${window.location.origin}/signup/continue`);
            return;
        }
    }
    handOffNow(leaving);
}

/** The hand-over itself, with no more looking. */
export function handOffNow({ target, organizationName, endSignup }: Leaving): void {
    if (endSignup) endSignupSession();
    window.location.href = handoffUrl(target, organizationName);
}

/** On the way back from ending the other session: where to go, once, if it's recent. */
export function takeWaitingHandOff(now = Date.now()): Leaving | undefined {
    const raw = tabStore.get(WAITING);
    tabStore.remove(WAITING);
    try {
        const w = JSON.parse(raw ?? 'null');
        if (!w || typeof w.target !== 'string' || typeof w.at !== 'number' || now - w.at >= HANDOFF_FRESH_MS) return undefined;
        return { target: w.target, organizationName: typeof w.organizationName === 'string' ? w.organizationName : undefined, endSignup: w.endSignup === true };
    } catch {
        return undefined;
    }
}
